import { booleanAttribute, Directive, ElementRef, input, numberAttribute, output } from "@angular/core";

@Directive({
  selector: "[clickOrDoubleClick]",
  host: {
    "(click)": "onHostClick($event)"
  }
})
export class ClickOrDoubleClickDirective {
  private static readonly DEFAULT_DELAY = 200;

  readonly clickDelay = input(ClickOrDoubleClickDirective.DEFAULT_DELAY, { transform: numberAttribute });
  readonly suppressNativeClick = input(true, { transform: booleanAttribute });

  readonly singleClick = output<MouseEvent>();
  readonly doubleClick = output<MouseEvent>();

  private _clickTimeout: any;
  private _clickCount = 0;

  constructor(private readonly _elementRef: ElementRef<HTMLElement>) {}

  protected onHostClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const hostElement = this._elementRef.nativeElement;

    // Check if the click is on an interactive child element (button, etc.)
    const isInteractiveChild = this.isInteractiveChild(target, hostElement);

    // If clicking on an interactive child, don't suppress or process
    if (isInteractiveChild) {
      return;
    }

    // Suppress native click behavior on the host element
    if (this.suppressNativeClick()) {
      event.preventDefault();
      event.stopImmediatePropagation();
      event.stopPropagation();
    }

    // Process the click for single/double click detection
    this.processClick(event);
  }

  private isInteractiveChild(target: HTMLElement | null, hostElement: HTMLElement): boolean {
    if (!target) {
      return false;
    }

    // If target is the host element itself, it's not a child
    if (target === hostElement) {
      return false;
    }

    // Check if target is a button or within a button
    const closestButton = target.closest("button");
    if (closestButton && closestButton !== hostElement && hostElement.contains(closestButton)) {
      return true;
    }

    // Check if target has ngbDropdownToggle or is within one
    const closestDropdown = target.closest("[ngbdropdowntoggle]");
    if (closestDropdown && closestDropdown !== hostElement && hostElement.contains(closestDropdown)) {
      return true;
    }

    // Check if target is an input, select, or textarea
    const tagName = target.tagName.toLowerCase();
    if (["input", "select", "textarea"].includes(tagName)) {
      return true;
    }

    return false;
  }

  private processClick(event: MouseEvent): void {
    this._clickCount++;

    if (this._clickCount === 1) {
      this._clickTimeout = setTimeout(() => {
        this.singleClick.emit(event);
        this._clickCount = 0;
      }, this.clickDelay());
    } else if (this._clickCount === 2) {
      clearTimeout(this._clickTimeout);
      this.doubleClick.emit(event);
      this._clickCount = 0;
    }
  }
}
